import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
import { artsCreativeIndustriesContent } from '../content/educational/artsCreativeIndustries';

// PDF Generation Function
const generatePDF = async (content, title, keywords) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 25;
    const contentWidth = pageWidth - (margin * 2);
    const headerHeight = 40;
    const footerHeight = 20;

    // Generate QR Codes
    const qrCodeHomepage = await QRCode.toDataURL('https://causeandimpact.org');
    const qrCodeApply = await QRCode.toDataURL('https://causeandimpact.org/apply');

    const addHeader = () => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Gradient title effect
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(24);
      
      // Blue shadow
      doc.setTextColor(37, 99, 235);
      doc.text('Cause & Impact, Inc.', (pageWidth / 2) + 0.5, 20.1, { align: 'center' });
      
      // Green overlay
      doc.setTextColor(5, 150, 105);
      doc.text('Cause & Impact, Inc.', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.text('Starting a Nonprofit Cause never got easier!', pageWidth / 2, 30, { align: 'center' });

      doc.addImage(qrCodeHomepage, 'PNG', pageWidth - margin - 25, 10, 20, 20);
    };

    const addFooter = (pageNumber) => {
      doc.setFillColor(17, 24, 39);
      doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.text(
        'Cause & Impact, Inc. | EIN: 84-3918881 | www.causeandimpact.org',
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
      doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    };

    const addCallToAction = (yPosition) => {
      const ctaHeight = 45;
      const qrSize = 30;
      doc.setFillColor(37, 99, 235);
      doc.roundedRect(margin, yPosition, contentWidth, ctaHeight, 5, 5, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.text('Ready to Get Started?', pageWidth / 2, yPosition + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.text('Visit causeandimpact.org/apply', pageWidth / 2, yPosition + 30, { align: 'center' });

      doc.addImage(qrCodeApply, 'PNG', pageWidth - qrSize - margin - 10, yPosition + 7.5, qrSize, qrSize);
    };

    const addContent = () => {
      let yPosition = headerHeight + 10;
      let pageNumber = 1;
    
      // Add title
      doc.setFontSize(24);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(title, contentWidth);
      doc.text(titleLines, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += (titleLines.length * 10) + 5;
    
      const handlePageBreak = () => {
        if (yPosition +22 > pageHeight - footerHeight) {
          doc.addPage();
          pageNumber++;
          addHeader();
          addFooter(pageNumber);
          yPosition = headerHeight + 10;
        }
      };
    
      content.forEach((section, index) => {
        handlePageBreak();
    
        // Section Heading
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(5, 150, 105);
        doc.text(section.heading, margin, yPosition);
        yPosition += 10;
    
        // Section Content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(31, 41, 55);
    
        const paragraphs = section.text.split('\n\n');
        paragraphs.forEach(paragraph => {
          const lines = paragraph.split('\n');
          
          lines.forEach(line => {
            handlePageBreak();
            
            // Check for level 3 headings
            if (line.trim().startsWith('### ')) {
              // Handle level 3 heading
              const headingText = line.trim().substring(4).trim().replace(/\*\*/g, '');
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(12);
              doc.setTextColor(5, 150, 105);
              doc.text(headingText, margin, yPosition);
              yPosition += 8;
              doc.setTextColor(31, 41, 55);
            }
            // Check for bullet points or numerical lists
            else if (line.trim().startsWith('-') || /^\d+\)/.test(line.trim())) {
              // Handle bullet points and numerical lists
              let listText;
              let listMarker;
              let markerWidth;
              
              // Calculate indentation level based on leading spaces
              const leadingSpaces = line.match(/^\s*/)[0].length;
              const indentationLevel = Math.floor(leadingSpaces / 2); // 2 spaces = 1 level
              const baseIndent = margin + 8;
              const levelIndent = 15; // Additional indent per level
              const currentIndent = baseIndent + (indentationLevel * levelIndent);
              
              if (line.trim().startsWith('-')) {
                // Bullet point
                listText = line.trim().substring(1).trim();
                listMarker = '•';
                markerWidth = 8;
              } else {
                // Numerical list (e.g., "1)", "2)")
                const match = line.trim().match(/^(\d+\))\s*(.*)/);
                listText = match[2];
                listMarker = match[1];
                doc.setTextColor(31, 41, 55);
                markerWidth = doc.getTextWidth(listMarker) + 2;
              }
              
              // List marker
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(11); // Consistent font size
              doc.text(listMarker, currentIndent, yPosition);
              
              // Reset text color and font for list content
              doc.setTextColor(31, 41, 55);
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(11); // Consistent font size
              
              // Adjust content width based on indentation
              const adjustedContentWidth = contentWidth - (indentationLevel * levelIndent) - 25;
              const listLines = doc.splitTextToSize(listText, adjustedContentWidth);
              listLines.forEach((bLine, idx) => {
                const parts = bLine.split(/(\*\*.*?\*\*)/);
                let currentX = currentIndent + markerWidth + 7; // Adjust text position after marker
                
                parts.forEach(part => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    doc.setFont('helvetica', 'bold');
                    const boldText = part.replace(/\*\*/g, '');
                    doc.text(boldText, currentX, yPosition);
                    currentX += doc.getTextWidth(boldText) + 1;
                  } else if (part.trim()) {
                    doc.setFont('helvetica', 'normal');
                    doc.text(part.trim(), currentX, yPosition);
                    currentX += doc.getTextWidth(part.trim()) + 1;
                  }
                });
                
                if (idx < listLines.length - 1) {
                  yPosition += 6; // Consistent line spacing within list items
                }
              });
              yPosition += 8; // Space after list item
            } else {
              // Regular text
              const wrappedLines = doc.splitTextToSize(line, contentWidth);
              wrappedLines.forEach((wrappedLine) => {
                const parts = wrappedLine.split(/(\*\*.*?\*\*)/);
                let currentX = margin;

                parts.forEach(part => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    doc.setFont('helvetica', 'bold');
                    const boldText = part.replace(/\*\*/g, '');
                    doc.text(boldText, currentX, yPosition);
                    currentX += doc.getTextWidth(boldText) + 1;
                  } else if (part.trim()) {
                    doc.setFont('helvetica', 'normal');
                    doc.text(part.trim(), currentX, yPosition);
                    currentX += doc.getTextWidth(part.trim()) + 1;
                  }
                });
                yPosition += 6;
                handlePageBreak();
              });
            }
          });
          
          yPosition += 2; // Consistent spacing between paragraphs
        });

        // Add Call to Action at the end of the last section
        if (index === content.length - 1) {
          yPosition += 10; // Add extra space before CTA
          const spaceForCTA = 50;
          if (yPosition + spaceForCTA > pageHeight - footerHeight) {
            doc.addPage();
            pageNumber++;
            addHeader();
            addFooter(pageNumber);
            yPosition = headerHeight + 10;
          }
          addCallToAction(yPosition);
        }
      });
    };

    // Generate PDF
    addHeader();
    addFooter(1);
    addContent();

    return doc;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return null;
  }
};

export const generateArtsCreativeIndustriesOverview = async () => {
  return generatePDF(
    artsCreativeIndustriesContent.sections,
    artsCreativeIndustriesContent.title,
    artsCreativeIndustriesContent.keywords
  );
}; 